import React, { useState, useRef, useEffect, useCallback } from "react";
import { Drawer, Form, Input, Typography, Button, Space, notification, Modal } from "antd";
import { Trans, useTranslation } from "react-i18next";
import { dummyData } from "./helper";
import moment from "moment-timezone";
const { Text } = Typography;

// Função auxiliar para realçar o texto
const highlightText = (text, highlight) => {
  if (!highlight) {
    return text;
  }
  const parts = text.split(new RegExp(`(${highlight})`, "gi"));
  return parts.map((part, i) => (part.toLowerCase() === highlight.toLowerCase() ? <mark key={i}>{part}</mark> : part));
};

function DebugConsoleDrawer({ debugConsole, setDebugConsole, updateIdentification }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentOccurrenceIndex, setCurrentOccurrenceIndex] = useState(0);
  const [occurrences, setOccurrences] = useState(null);
  const contentRef = useRef(null);

  // Usamos um useRef para armazenar todas as referências das ocorrências.
  // Será redefinido na fase de renderização para garantir que as referências estejam atualizadas.
  const allOccurrencesRefs = useRef([]);

  const jsonString = JSON.stringify(debugConsole.data, null, 2);
  const jsonLines = jsonString.split("\n");

  // Resetar allOccurrencesRefs.current antes de cada renderização para coletar as novas refs
  // Esta linha é crucial e deve estar fora do useEffect, mas antes do return/JSX
  allOccurrencesRefs.current = [];

  // ---
  // Lógica de Navegação das Ocorrências
  // ---
  const goToNextOccurrence = useCallback(() => {
    if (allOccurrencesRefs.current.length > 0) {
      setCurrentOccurrenceIndex((prevIndex) => (prevIndex + 1) % allOccurrencesRefs.current.length);
    }
  }, []);

  const goToPreviousOccurrence = useCallback(() => {
    if (allOccurrencesRefs.current.length > 0) {
      setCurrentOccurrenceIndex((prevIndex) => (prevIndex - 1 + allOccurrencesRefs.current.length) % allOccurrencesRefs.current.length);
    }
  }, []);

  // ---
  // Efeito para Rolar para a Ocorrência Atual
  // ---
  useEffect(() => {
    // Garante que o índice esteja dentro dos limites das ocorrências encontradas
    const validIndex = Math.max(0, Math.min(currentOccurrenceIndex, allOccurrencesRefs.current.length - 1));

    setOccurrences(allOccurrencesRefs.current.length);
    const targetOccurrence = allOccurrencesRefs.current[validIndex];

    if (targetOccurrence) {
      targetOccurrence.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentOccurrenceIndex, searchTerm, debugConsole.data]); // Dependências: índice atual, termo de busca e dados (para reavaliar as ocorrências)

  // ---
  // Manipulador de Pesquisa
  // ---
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentOccurrenceIndex(0); // Resetar para a primeira ocorrência ao mudar o termo de busca
  };

  const handleClearLocalStorage = () => {
    Modal.confirm({
      title: t("debug.clearMemoryTitle"),
      content: t("debug.clearMemoryContent"),
      onOk: handleClearLocalStorageAction,
    });
  };

  const populateDummyData = () => {
    const data = dummyData();

    updateIdentification({
      name: data.name,
      email: data.email,
      phone: data.phone,
    });

    notification.success({
      message: t("checkout.dummyDataSuccess"),
      description: t("checkout.dummyDataDescription", { name: data.name, email: data.email, phone: data.phone }),
    });

    setDebugConsole({ visible: false, data: null });
  };

  const handleClearLocalStorageAction = () => {
    try {
      const prefix = "SalesPark-Checkout-v2";
      let cleanedCount = 0;

      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          localStorage.removeItem(key);
          cleanedCount++;
        }
      }
      // Assumindo que 'message' do Ant Design foi importado e está disponível no escopo
      notification.success({ message: t("debug.clearedItems", { count: cleanedCount }) });

      // reload page
      window.location.reload();
      // Opcional: Se 'setDebugConsole' for uma prop ou função disponível no escopo do componente,
      // você pode chamá-la aqui para atualizar o estado ou fechar a drawer.
      // Exemplo: setDebugConsole({ visible: false, data: null });
    } catch (error) {
      console.error("Erro ao limpar localStorage:", error);
      // Assumindo que 'message' do Ant Design foi importado e está disponível no escopo
      notification.error({ message: t("debug.clearMemoryError") });
    }
  };

  return (
    <Drawer
      height={"550px"}
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Debug Console</span>
          <Space>
            <Button type="primary" onClick={() => window.alert(t("debug.featureNotImplemented"))}>
              {t("debug.markAsPaid")}
            </Button>
            <Button type="default" onClick={() => populateDummyData()}>
              {t("checkout.dummyDataTooltip")}
            </Button>
          </Space>
        </div>
      }
      placement="bottom"
      onClose={() => setDebugConsole({ visible: false, data: null })}
      visible={debugConsole.visible}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <Button type="primary" danger onClick={() => handleClearLocalStorage()}>
              {t("debug.clearMemory")}
            </Button>
          </div>
          <Button type="primary" onClick={() => setDebugConsole({ visible: false, data: null })}>
            {t("product.close")}
          </Button>
        </div>
      }
    >
      <p>
        <strong>{t("legal.purchaseId")}</strong> <Text copyable>{debugConsole.data?.purchase_id}</Text>
      </p>
      <Form layout="inline">
        <Form.Item label={t("debug.search")}>
          <Input value={searchTerm} onChange={handleSearchChange} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button onClick={goToPreviousOccurrence} disabled={!searchTerm}>
              {t("debug.previous")}
            </Button>
            <Button onClick={goToNextOccurrence} disabled={!searchTerm}>
              {t("debug.next")}
            </Button>
            <span>
              <Trans i18nKey="debug.occurrences" values={{ count: occurrences }} components={{ strong: <strong /> }} />
            </span>
          </Space>
        </Form.Item>
      </Form>
      <div style={{ marginTop: "10px" }}>
        <pre ref={contentRef} className="debug-drawer-pre" style={{ margin: 0, maxHeight: "369px", overflowY: "auto" }}>
          {jsonLines.map((line, lineIndex) => {
            const lineContainsSearchTerm = line.toLowerCase().includes(searchTerm.toLowerCase());

            if (lineContainsSearchTerm && searchTerm) {
              const parts = line.split(new RegExp(`(${searchTerm})`, "gi"));
              return (
                <span key={lineIndex} style={{ display: "block" }}>
                  {parts.map((part, partIndex) => {
                    const isMatch = part.toLowerCase() === searchTerm.toLowerCase();
                    const refCallback = (el) => {
                      if (el && isMatch) {
                        // Adiciona a referência SÓ se for uma ocorrência realçada
                        // O array allOccurrencesRefs.current é esvaziado no início da renderização
                        // e preenchido aqui.
                        allOccurrencesRefs.current.push(el);
                      }
                    };

                    return isMatch ? (
                      <mark key={partIndex} ref={refCallback}>
                        {part}
                      </mark>
                    ) : (
                      <span key={partIndex}>{part}</span>
                    );
                  })}
                  {lineIndex < jsonLines.length - 1 && "\n"}
                </span>
              );
            } else {
              return (
                <span key={lineIndex} style={{ display: "block" }}>
                  {line}
                  {lineIndex < jsonLines.length - 1 && "\n"}
                </span>
              );
            }
          })}
        </pre>
      </div>
    </Drawer>
  );
}

export default DebugConsoleDrawer;
